export const TOPICS = [
  {
    slug: "luxury-residential",
    title: "Luxury Residential",
    tag: "High-value estate assessment",
    number: "01",
    image:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=70&auto=format&fit=crop",
    lede:
      "Pre-loss intelligence engineered for homes valued at $1.5M and above — where a single unverified detail can cost everything.",
    visual: "luxury",
    sections: {
      why: {
        title: "Why Luxury Residential Risk Is Different",
        body:
          "High-value estates carry exposure that generic insurance documentation never addresses. Custom finishes, imported materials, security systems, specialty HVAC, historic features, waterfront components, and estate-level legal structures all require documentation built for their real replacement cost and litigation value.",
      },
      documents: {
        title: "What Before The Risk Documents",
        items: [
          "Full exterior structure and envelope condition",
          "Roof system, flashing, and vulnerability points",
          "Windows, doors, and forced-entry/impact exposure",
          "Foundation integrity and water-intrusion history",
          "Custom interior finishes and specialty installations",
          "Specialty systems (HVAC, pool/spa, smart-home, security)",
          "Grounds, seawalls, docks, and outbuildings",
          "Photographic chain-of-evidence for each asset",
        ],
      },
      benefits: {
        title: "Benefits for High-Value Homeowners",
        items: [
          { label: "Establish True Value", desc: "Document condition and value before exposure." },
          { label: "Insurance Leverage", desc: "Pre-loss evidence when negotiating payout." },
          { label: "Estate Protection", desc: "Defensible files for legal & estate purposes." },
          { label: "Rapid Recovery", desc: "Accelerate claim resolution when loss occurs." },
        ],
      },
    },
  },
  {
    slug: "commercial-property",
    title: "Commercial Property",
    tag: "Exposure mapping & risk scoring",
    number: "02",
    image:
      "https://images.unsplash.com/photo-1554469384-e58fac16e23a?w=800&q=70&auto=format&fit=crop",
    lede:
      "Risk intelligence for commercial owners who need clarity, documentation, and preparation before liability becomes costly.",
    visual: "commercial",
    sections: {
      why: {
        title: "Why Commercial Risk Demands Pre-Loss Documentation",
        body:
          "Commercial properties aggregate tenant, vendor, and operational exposure. Business-interruption alone can dwarf structural loss. Pre-loss files create the evidentiary backbone that protects owners when a claim, liability dispute, or tenant action arises.",
      },
      documents: {
        title: "What We Document on Commercial Assets",
        items: [
          "Structural envelope, roof systems, and load paths",
          "Mechanical, electrical, plumbing (MEP) systems",
          "Fire, life-safety, and compliance documentation",
          "Tenant-space condition and lease exposure",
          "Common-area liability and signage integrity",
          "Parking, walkways, and ADA compliance",
          "Environmental risk and drainage mapping",
          "Operational business-interruption exposure",
        ],
      },
      benefits: {
        title: "Benefits for Commercial Owners & Asset Managers",
        items: [
          { label: "Faster Claim Recovery", desc: "Condition baseline speeds resolution." },
          { label: "Liability Shielding", desc: "Pre-loss files counter nuisance claims." },
          { label: "Capital Planning", desc: "Risk scoring informs reserve strategy." },
          { label: "Investor Confidence", desc: "Defensible documentation for partners." },
        ],
      },
    },
  },
  {
    slug: "legal-documentation",
    title: "Legal Documentation",
    tag: "Defensible case files",
    number: "03",
    image:
      "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=800&q=70&auto=format&fit=crop",
    lede:
      "Organized, defensible, strategically structured documentation for insurance disputes, property risk, and liability.",
    visual: "legal",
    sections: {
      why: {
        title: "Why Documentation Decides Cases",
        body:
          "In property disputes, whoever controls the evidentiary record controls the outcome. Before The Risk assembles pre-loss, mid-loss, and post-loss documentation in formats that survive cross-examination and accelerate resolution.",
      },
      documents: {
        title: "What Legal Documentation Packages Include",
        items: [
          "Condition baseline with timestamped photography",
          "Causation narrative and evidence-preservation log",
          "Liability map and third-party contribution analysis",
          "Itemized damages, estimates, and scope of work",
          "Expert declarations and engineering findings",
          "Carrier correspondence and claim history",
          "Exhibit-ready visuals for trial or mediation",
          "Litigation-ready narrative and case summary",
        ],
      },
      benefits: {
        title: "Benefits for Attorneys & Legal Teams",
        items: [
          { label: "Trial-Ready Format", desc: "Exhibits indexed for instant reference." },
          { label: "Stronger Settlement", desc: "Evidence weight drives better outcomes." },
          { label: "Expert Coordination", desc: "Engineers & estimators pre-aligned." },
          { label: "Reduced Prep Time", desc: "Structured files save billable hours." },
        ],
      },
    },
  },
  {
    slug: "hoa-condominium",
    title: "HOA & Condominium",
    tag: "Board-level risk strategy",
    number: "04",
    image:
      "https://images.unsplash.com/photo-1464082354059-27db6ce50048?w=800&q=70&auto=format&fit=crop",
    lede:
      "Strategic consulting for communities managing shared assets, insurance exposure, and board-level risk decisions.",
    visual: "hoa",
    sections: {
      why: {
        title: "Why HOAs and Condos Need Pre-Loss Intelligence",
        body:
          "Community associations hold fiduciary responsibility for shared assets worth tens or hundreds of millions. Pre-loss documentation protects the board from personal exposure and arms the community with the evidentiary leverage to act decisively in disputes.",
      },
      documents: {
        title: "Shared Assets We Document",
        items: [
          "Exterior building envelope and façade",
          "Roofing systems and drainage",
          "Common-area interiors and lobbies",
          "Amenity spaces (clubhouse, pool, gym)",
          "Parking garages, decks, and paved surfaces",
          "Mechanical rooms, elevators, and life-safety",
          "Landscaping, seawalls, and community perimeter",
          "Reserve-item condition and useful-life scoring",
        ],
      },
      benefits: {
        title: "Benefits for Boards, Managers & Owners",
        items: [
          { label: "Board Protection", desc: "Fiduciary record of diligence." },
          { label: "Reserve Accuracy", desc: "Condition-based planning, not guesswork." },
          { label: "Insurance Posture", desc: "Leverage before renewal or dispute." },
          { label: "Owner Confidence", desc: "Transparent community risk reporting." },
        ],
      },
    },
  },
  {
    slug: "property-inspection",
    title: "Property Inspection",
    tag: "Field documentation & assessment",
    number: "05",
    image:
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=70&auto=format&fit=crop",
    lede:
      "Field-level documentation engineered for litigation pressure — not a checklist, a chain of evidence.",
    visual: "inspection",
    sections: {
      why: {
        title: "Why Inspection Quality Decides the Record",
        body:
          "The inspection is where pre-loss intelligence is earned or lost. Every photograph, measurement, and note becomes evidence. Our methodology is built for the moment a report is challenged in a claim, a deposition, or a courtroom.",
      },
      documents: {
        title: "Our Inspection Methodology",
        items: [
          "Systematic exterior-to-interior traversal",
          "Grid-based photographic chain-of-evidence",
          "Drone documentation for roofs and elevations",
          "Thermal, moisture, and environmental readings",
          "Measurement capture for estimate integrity",
          "Expert coordination (engineer, contractor, trade)",
          "Time-stamped, geo-tagged file management",
          "Multi-reviewer quality assurance",
        ],
      },
      benefits: {
        title: "Benefits Across Stakeholders",
        items: [
          { label: "Defensibility", desc: "Every asset captured, nothing assumed." },
          { label: "Efficiency", desc: "Structured capture reduces revisits." },
          { label: "Expert Integration", desc: "Specialists aligned from day one." },
          { label: "Audit Trail", desc: "Full provenance for every file." },
        ],
      },
    },
  },
  {
    slug: "claims-reports",
    title: "Claims & Reports",
    tag: "Litigation-ready evidence",
    number: "06",
    image:
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=70&auto=format&fit=crop",
    lede:
      "Risk reports and claim files engineered for executives, boards, and legal teams — structured, defensible, decisive.",
    visual: "reports",
    sections: {
      why: {
        title: "Why the Report Is the Product",
        body:
          "A pre-loss file is only as powerful as the report that communicates it. Before The Risk reports are structured for executive decision-making, legal defensibility, and carrier pressure — organized so every data point earns its place.",
      },
      documents: {
        title: "Inside a Before The Risk Report",
        items: [
          "Executive summary with risk score",
          "Condition baseline and asset inventory",
          "Photographic evidence blocks, indexed",
          "Estimate lines with Xactimate detail",
          "Engineering and expert findings",
          "Exposure flags and recommended actions",
          "Strategic notes for legal or board review",
          "Appendix of raw files and metadata",
        ],
      },
      benefits: {
        title: "Benefits of the Finished Report",
        items: [
          { label: "Executive-Ready", desc: "Decisions in minutes, not meetings." },
          { label: "Court-Ready", desc: "Indexed for exhibit reference." },
          { label: "Carrier-Ready", desc: "Counters adjuster pressure with data." },
          { label: "Board-Ready", desc: "Risk score the whole board understands." },
        ],
      },
    },
  },
  {
    slug: "business-operations",
    title: "Cybersecurity & AI",
    tag: "Business operations review",
    number: "07",
    image:
      "https://images.unsplash.com/photo-1759661881353-5b9cc55e1cf4?w=800&q=70&auto=format&fit=crop",
    lede:
      "A business operations review for the risks you cannot see — cyber exposure, data integrity, and safe, practical AI implementation.",
    visual: "operations",
    sections: {
      why: {
        title: "Why Operational Risk Is Now Property Risk",
        body:
          "Loss is no longer only wind, water, or fire. A compromised inbox, a ransomware event, or a lost records system can halt operations, destroy evidence, and void a claim position overnight. Before The Risk applies the same pre-loss discipline to your digital operations — and helps you adopt AI without creating new exposure.",
      },
      documents: {
        title: "What the Operations Review Covers",
        items: [
          "Account, password, and multi-factor authentication review",
          "Email security and phishing exposure assessment",
          "Backup, recovery, and records-retention verification",
          "Device, network, and remote-access posture",
          "Vendor and third-party access mapping",
          "Cyber insurance requirements and coverage alignment",
          "AI opportunity mapping for repetitive workflows",
          "Secure AI implementation plan and usage policy",
        ],
      },
      benefits: {
        title: "Benefits for Owners & Operators",
        items: [
          { label: "Reduced Exposure", desc: "Close the gaps attackers rely on." },
          { label: "Protected Evidence", desc: "Records that survive any disruption." },
          { label: "Insurability", desc: "Meet carrier security requirements." },
          { label: "AI Efficiency", desc: "Save hours safely with guided AI." },
        ],
      },
    },
  },
];

export const getTopic = (slug) => TOPICS.find((t) => t.slug === slug);
export const otherTopics = (slug) => TOPICS.filter((t) => t.slug !== slug);
